// @givmo/org-console — the *Deactivate organization* control: the card and the confirm dialog
// (Org Machinery OM-9, D-CPO-32; placed by D-CPO-33, approved for now by D-CPO-34).
//
// ══ THE CARD IS THE ENTRY; THE DIALOG IS THE ACT ═══════════════════════════════════════════════
//
// The control lives in the Organization section of the gear's settings page, at its foot, for a
// holder of the `deactivate_org` capability on the ONE standing read — a capability string, never
// an admin flag (the build law: permission checks, never admin checks). The card is quiet: the
// organization's own name, one sentence, a neutral button — the settings-page convention for an act
// taken once, deliberately. Red is reserved for the dialog's confirm, and it turns red only once the
// typed name matches.
//
// ══ THE CONSEQUENCES ARE THE HOST'S DATA ═══════════════════════════════════════════════════════
//
// What the act does differs per entity class, and this package never learns the class. The host
// hands the sentences in through `vocab.deactivation` — the class noun for the running copy and the
// consequences in reading order, the class's re-enrollment truth last — and this file renders them
// verbatim. Nothing here names a class (`org-generic.test.ts`).
//
// ══ "NOTHING IS DELETED" HAS ONE EXCEPTION, SAID WHERE IT IS TRUE ═════════════════════════════
//
// The act clears the physical address the organization entered (D-CPO-62: it may be a person's
// home), shown on the page or not: D-CPO-68 can hide it while keeping its value, and the act clears
// it all the same. So the card's line, and the host's matching consequence, say so wherever one was
// entered — "Nothing is deleted except the physical address you entered." — and keep "Nothing is
// deleted." everywhere else (D-CPO-69 question 8, its words, widened by OM-92; the dialog's line
// follows the card's, its sibling). The page reads the condition off the settings read it already
// holds: the server's `entered_address_on_file`, told only to someone who may deactivate, or, from
// a backend that tells nothing, the facts the page shows. The card waits for that read to answer
// before it says either.
//
// ══ THE ACT ENDS THE CALLER'S OWN SESSION ══════════════════════════════════════════════════════
//
// A 200 is the caller's last authenticated response: every membership ends at commit and the server
// ends every session after it, the caller's included. So the dialog says so before the act, refuses
// dismissal while the act is in flight or done (there is nothing to go back to), shows the counts
// the wire answered, holds the done panel long enough to be read, and then hands the host
// `onDeactivated` — the host signs the person out and takes them to its class door. The hold is a
// courtesy and the sign-out is the consequence: a navigation that unmounts the dialog during the
// hold still hands the host the sign-out. A refusal keeps the dialog open with one line and nothing
// changed.
//
// ══ THE TYPED CONFIRMATION IS QUIET ════════════════════════════════════════════════════════════
//
// The person types the organization's name — case-insensitive, whitespace-trimmed. A mismatch keeps
// the destructive button disabled and says nothing: an error line under a field someone is still
// typing into is noise, and the disabled button already answers.
//
// DATA CONTRACT: the `org-deactivation-*` test-id family (fixed, not prefixed by the mount).

import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  TextField,
  testIdProp,
} from '@givmo/design-system';
import type {
  OrgConsoleConfig,
  OrgDeactivationConsequence,
  OrgDeactivationCopy,
} from './org-console-config';
import { useDeactivateOrg } from './use-org-console';

export const ORG_DEACTIVATION_TEST_ID = 'org-deactivation';
const PREFIX = ORG_DEACTIVATION_TEST_ID;

/** The control's word everywhere it renders — the card's button and the dialog's confirm. */
export const DEACTIVATE_CONTROL_LABEL = 'Deactivate organization';
/** The line under the typed confirmation — the caller's own session ends with the act. */
export const DEACTIVATION_SIGNED_OUT_NOTE = 'You will be signed out when this completes.';
/** The done panel's last line, while the host takes the person to the door. */
export const DEACTIVATION_SIGNING_OUT_LINE = 'Signing you out…';
/** How long the done panel stays on screen before `onDeactivated` fires — long enough to be read. */
export const DEACTIVATION_DONE_HOLD_MS = 1400;

/**
 * The card's one line (D-CPO-69 question 8): what the act deletes — nothing, or, where the
 * organization entered a physical address, shown on the page or not (OM-92), that address alone.
 */
export function deactivationCardBody(noun: string, clearsEnteredAddress = false): string {
  const deleted = clearsEnteredAddress
    ? 'Nothing is deleted except the physical address you entered.'
    : 'Nothing is deleted.';
  return `Freezes the ${noun}’s Givmo account and signs everyone out, including you. ${deleted}`;
}

/** One of the host's consequences, as it reads on this page (its conditional line resolved). */
export function deactivationConsequenceLine(
  consequence: OrgDeactivationConsequence,
  clearsEnteredAddress: boolean,
): string {
  if (typeof consequence === 'string') return consequence;
  return clearsEnteredAddress ? consequence.whereEnteredAddress : consequence.line;
}

/** "N people were signed out." — the collective noun is people, never a role word. */
export function signedOutCountLine(signedOut: number): string {
  return signedOut === 1 ? '1 person was signed out.' : `${signedOut} people were signed out.`;
}

/** The honest second line when the server could not end every session yet. */
export function pendingSessionsLine(failures: number): string {
  return failures === 1
    ? '1 session couldn’t be ended yet. It holds no access and ends on its next request.'
    : `${failures} sessions couldn’t be ended yet. They hold no access and end on their next request.`;
}

/** The one failure line — a refusal and a transport failure read the same: nothing changed. */
export function deactivationFailureLine(noun: string): string {
  return `We couldn’t deactivate the ${noun}. Nothing was changed — please try again.`;
}

/** The typed-name gate: case-insensitive, whitespace-trimmed; an unknown name never matches. */
export function nameMatchesOrganization(typed: string, orgName: string): boolean {
  const wanted = orgName.trim().toLowerCase();
  return wanted.length > 0 && typed.trim().toLowerCase() === wanted;
}

/** The copy and the name the control needs, or `null` when the host has not supplied both. */
function controlInputs(
  config: OrgConsoleConfig,
): { copy: OrgDeactivationCopy; orgName: string } | null {
  const copy = config.vocab.deactivation;
  const orgName = (config.vocab.orgName ?? '').trim();
  if (copy === undefined || orgName.length === 0) return null;
  return { copy, orgName };
}

export interface OrgDeactivateDialogProps {
  config: OrgConsoleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The host's sign-out and door hop, called ONCE after the done panel's hold. The host clears the
   * client session and lands the person on its plain class sign-in door — no notice.
   */
  onDeactivated: () => void;
  /**
   * Whether the page shows a physical address the organization entered — the one thing the act
   * deletes (D-CPO-62), so a conditional consequence says so (D-CPO-69 question 8).
   */
  clearsEnteredAddress?: boolean;
}

export function OrgDeactivateDialog({
  config,
  open,
  onOpenChange,
  onDeactivated,
  clearsEnteredAddress = false,
}: OrgDeactivateDialogProps) {
  const inputs = controlInputs(config);
  const [typed, setTyped] = useState('');
  const deactivate = useDeactivateOrg(config.orgRef);
  const onDeactivatedRef = useRef(onDeactivated);
  useEffect(() => {
    onDeactivatedRef.current = onDeactivated;
  });

  // A fresh dialog every time it opens: a name typed for a previous attempt must not arm the button.
  useEffect(() => {
    if (!open) return;
    setTyped('');
    deactivate.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prime on open only
  }, [open]);

  // The act landed: hold the done panel, then hand the host the sign-out. The timer is armed once
  // per landing, whatever the host's callback identity does meanwhile.
  //
  // ⚠ THE SIGN-OUT IS NOT CANCELLABLE ONCE THE ACT HAS LANDED. The dialog refuses dismissal while
  // done, but it cannot refuse history: the browser's Back button (a swipe back on a phone) during
  // the hold unmounts this dialog, and a cleanup that only cleared the timer left a signed-in shell
  // on an organization the server had already closed — every membership and session ended at the
  // 200 while the client kept its own session and cache. So the cleanup fires the sign-out itself
  // when the hold has not: the hold is a courtesy, the sign-out is the act's consequence. Fired at
  // most once either way.
  useEffect(() => {
    if (!deactivate.isSuccess) return;
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      onDeactivatedRef.current();
    };
    const timer = window.setTimeout(fire, DEACTIVATION_DONE_HOLD_MS);
    return () => {
      window.clearTimeout(timer);
      fire();
    };
  }, [deactivate.isSuccess]);

  if (inputs === null) return null;
  const { copy, orgName } = inputs;
  const pending = deactivate.isPending;
  const done = deactivate.isSuccess;
  const locked = pending || done;
  const matches = nameMatchesOrganization(typed, orgName);
  const result = deactivate.data ?? null;
  const title = done ? `${orgName} is deactivated` : `Deactivate ${orgName}?`;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Not dismissible while the act is in flight or done — there is nothing to go back to.
        if (!next && locked) return;
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={title}
        width={480}
        className="flex flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        {done ? (
          <div className="flex flex-col gap-8" role="status" {...testIdProp(`${PREFIX}-done`)}>
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              {title}
            </Dialog.Title>
            {result !== null && (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-done-count`)}
              >
                {signedOutCountLine(result.sessions_revoked)}
              </p>
            )}
            {result !== null && result.session_failures > 0 && (
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-done-partial`)}
              >
                {pendingSessionsLine(result.session_failures)}
              </p>
            )}
            <p
              className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
              {...testIdProp(`${PREFIX}-done-signing-out`)}
            >
              {DEACTIVATION_SIGNING_OUT_LINE}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-6">
              <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
                {title}
              </Dialog.Title>
              <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                This freezes {orgName} on Givmo.
              </Dialog.Description>
            </div>

            <ul
              className="flex list-disc flex-col gap-6 pl-18 marker:text-text-tertiary"
              {...testIdProp(`${PREFIX}-consequences`)}
            >
              {copy.consequences
                .map((consequence) =>
                  deactivationConsequenceLine(consequence, clearsEnteredAddress),
                )
                .map((line) => (
                  <li
                    key={line}
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                  >
                    {line}
                  </li>
                ))}
            </ul>

            <TextField
              label={`To confirm, type ${orgName}`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={pending}
              testId={`${PREFIX}-confirm-input`}
            />

            {deactivate.isError && (
              <ErrorMessage testId={`${PREFIX}-error`}>
                {deactivationFailureLine(copy.noun)}
              </ErrorMessage>
            )}

            <p
              className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
              {...testIdProp(`${PREFIX}-note`)}
            >
              {DEACTIVATION_SIGNED_OUT_NOTE}
            </p>

            <div className="flex items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="secondary"
                size="md"
                disabled={pending}
                onClick={() => onOpenChange(false)}
                testId={`${PREFIX}-cancel`}
              >
                Cancel
              </Button>
              <Button
                appearance="saas"
                tone="danger"
                size="md"
                loading={pending}
                disabled={!matches}
                onClick={() => deactivate.mutate()}
                testId={`${PREFIX}-confirm`}
              >
                {DEACTIVATE_CONTROL_LABEL}
              </Button>
            </div>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

export interface OrgDeactivationCardProps {
  config: OrgConsoleConfig;
  /** See {@link OrgDeactivateDialogProps.onDeactivated}. */
  onDeactivated: () => void;
  /** See {@link OrgDeactivateDialogProps.clearsEnteredAddress}: the card's line and the dialog's. */
  clearsEnteredAddress?: boolean;
}

/**
 * The control at rest: the organization's name, one sentence, a neutral button that opens the
 * dialog. Renders NOTHING until the host has supplied both the class copy and the organization's
 * resolved name — the typed confirmation is against that name, so a card with no name to type
 * would be a door onto a gate.
 */
export function OrgDeactivationCard({
  config,
  onDeactivated,
  clearsEnteredAddress = false,
}: OrgDeactivationCardProps) {
  const [open, setOpen] = useState(false);
  const inputs = controlInputs(config);
  if (inputs === null) return null;
  const { copy, orgName } = inputs;
  return (
    <>
      <Card testId={`${PREFIX}-card`}>
        <CardBody className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16">
          <div className="flex min-w-0 flex-col gap-4">
            <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              Deactivate {orgName}
            </h3>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {deactivationCardBody(copy.noun, clearsEnteredAddress)}
            </p>
          </div>
          <Button
            appearance="saas"
            tone="secondary"
            size="md"
            className="shrink-0 self-start sm:self-auto"
            onClick={() => setOpen(true)}
            testId={`${PREFIX}-open`}
          >
            {DEACTIVATE_CONTROL_LABEL}
          </Button>
        </CardBody>
      </Card>
      <OrgDeactivateDialog
        config={config}
        open={open}
        onOpenChange={setOpen}
        onDeactivated={onDeactivated}
        clearsEnteredAddress={clearsEnteredAddress}
      />
    </>
  );
}
